export function normalizeAppRole(value) {
  const raw = String(value || '').trim();
  const key = raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

  if (!key) return '';
  if (['superadmin', 'superadministrador', 'admin', 'administrador', 'administrator'].includes(key)) return 'super_admin';
  if (['admindepo', 'admindeposito', 'deposito', 'deposit', 'logistica', 'logistics'].includes(key)) return 'admin_depo';
  if (['adminrrhh', 'rrhh', 'rh', 'hr', 'recursoshumanos', 'humanresources'].includes(key)) return 'admin_rrhh';
  if (['ventas', 'sales', 'comercial'].includes(key)) return 'ventas';
  if (['referentes', 'referente', 'responsables', 'responsable'].includes(key)) return 'referentes';
  if (['logistico', 'logisticos', 'logisticausuario', 'operadores', 'operador', 'viewer', 'visualizador', 'lectura', 'readonly'].includes(key)) return 'logistico';

  return raw.toLowerCase();
}

export function hasAnyRole(userRole, roles = []) {
  const normalizedRole = normalizeAppRole(userRole);
  return roles.map(normalizeAppRole).includes(normalizedRole);
}

export function permissionLevel(user, resourceKey) {
  if (normalizeAppRole(user?.role) === 'super_admin') return 'write';
  return user?.permissions?.[resourceKey] || 'none';
}

export function canRead(user, resourceKey) {
  return ['read', 'write'].includes(permissionLevel(user, resourceKey));
}

export function canWrite(user, resourceKey) {
  return permissionLevel(user, resourceKey) === 'write';
}

export function canReadAny(user, resourceKeys = []) {
  return resourceKeys.some(resourceKey => canRead(user, resourceKey));
}

export function defaultPathForRole(role) {
  return normalizeAppRole(role) === 'admin_rrhh' ? '/rrhh' : '/';
}

export function defaultPathForUser(user) {
  if (!user) return '/login';
  const rrhhModuleResources = [
    'rrhh.plan-semanal', 'rrhh.plan-mensual',
    'rrhh.tareas',
    'rrhh.incidencias',
    'rrhh.jornales',
    'rrhh.operaciones',
    'rrhh.dashboard',
    'rrhh.reportes',
    'rrhh.analisis',
    'rrhh.mis-marcas',
    'rrhh.configuracion',
  ];
  const sueldosModuleResources = [
    'rrhh.liquidacion',
    'rrhh.remuneraciones',
    'rrhh.liquidacion-parametros',
    'rrhh.liquidacion-cierre',
    'rrhh.configuracion',
  ];
  const preferred = [
    ['agenda', '/'],
    ['agenda.equipos', '/equipos'],
    ['logistica', '/logistics'],
    ['inventario.stock', '/inventory/stock'],
    ['inventario.racks', '/inventory/racks'],
    ['inventario.packs', '/inventory/packs'],
    ['inventario.incidencias', '/inventory/incidencias'],
    ['ventas.cotizaciones', '/ventas/cotizaciones'],
    ['configuracion.personal', '/configuracion/personal'],
    ['configuracion.biometria', '/configuracion/biometria'],
  ];
  const match = preferred.find(([resource]) => canRead(user, resource));
  if (!match && canReadAny(user, rrhhModuleResources)) return '/rrhh';
  if (!match && canReadAny(user, sueldosModuleResources)) return '/sueldos';
  if (!match && canRead(user, 'rrhh.reloj')) return '/rrhh/reloj';
  return match?.[1] || defaultPathForRole(user.role);
}

export function roleLabel(role) {
  const labels = {
    super_admin: 'Super admin',
    admin_depo: 'Admin depo',
    admin_rrhh: 'Admin RRHH',
    ventas: 'Ventas',
    referentes: 'Referentes',
    logistico: 'Logístico',
  };
  return labels[normalizeAppRole(role)] || role || '';
}
